import { useCallback } from "react";
import CompletedIcon from "../../../../../assets/svg/completed-icon";
import CrossIcon from "../../../../../assets/svg/v1/cross.icon";
import EditIcon from "../../../../../assets/svg/edit-icon";
import { useAppDispatch } from "../../../../../redux/store";
import { setDrawer } from "../../../../../redux/slices/drawerSlice";
import type { CrmViewItem } from "../../../../../redux/slices/crmLeadListSlice";

interface CrmViewTabsProps {
  views: CrmViewItem[];
  selectedViewId: string;
  onSelectView: (viewId: string) => void;
  onDeleteView?: (view: CrmViewItem) => void;
}

export const CrmViewTabs = ({
  views,
  selectedViewId,
  onSelectView,
  onDeleteView,
}: CrmViewTabsProps) => {
  const dispatch = useAppDispatch();

  const handleSelectTab = useCallback(
    (tabId: string) => {
      onSelectView(tabId);
    },
    [onSelectView]
  );


  return (
    <div className="flex items-center gap-[6px] min-w-0">
      {views?.length > 0 && (
        <>
          <div className="h-4 w-px bg-border-gray dark:bg-border-gray-dk shrink-0 ml-0.5" />
          <div className="flex items-center gap-[6px] min-w-0 overflow-x-auto overflow-y-hidden no-scrollbar">
            {views?.map((tab) => {
              const isSelected = selectedViewId === tab?.id;
              return (
                <button
                  key={tab?.id}
                  onClick={() => handleSelectTab(tab?.id)}
                  className={`group flex items-center h-[26px] px-[6px] rounded-md border transition-all duration-150 cursor-pointer shrink-0 ${
                    isSelected
                      ? "gap-2 bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border-crm-card-border dark:border-crm-card-border-dk"
                      : "gap-0 hover:gap-2 focus-within:gap-2 bg-transparent border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    {tab?.emoji_icon && (
                      <span className="text-md leading-none shrink-0">
                        {tab?.emoji_icon}
                      </span>
                    )}
                    <span className="text-sm whitespace-nowrap text-verba-indigo dark:text-verba-indigo-dk">
                      {tab?.name}
                    </span>
                  </div>

                  <div
                    className={`flex items-center shrink-0 transition-[gap] duration-150 ${
                      isSelected
                        ? "gap-1"
                        : "gap-0 group-hover:gap-1 group-focus-within:gap-1"
                    }`}
                  >
                    <span
                      className={`h-[14px] min-w-0 overflow-hidden shrink-0 flex items-center justify-center transition-[width,opacity] duration-150 mb-px ${
                        isSelected
                          ? "w-[14px] opacity-100 pointer-events-auto"
                          : "w-0 opacity-0 pointer-events-none group-hover:w-[14px] group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:w-[14px] group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(
                            setDrawer({
                              open: true,
                              type: "crm_edit_view",
                              id: tab.id,
                            })
                          );
                        }}
                        className="flex items-center justify-center p-0 border-0 bg-transparent cursor-pointer"
                        aria-label={`Edit view ${tab.name}`}
                      >
                        <EditIcon className="w-[14px] h-[14px] shrink-0 text-dark-gray dark:text-dark-gray-dk" />
                      </button>
                    </span>

                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteView?.(tab);
                      }}
                      className={`cursor-pointer h-[10px] min-w-0 overflow-hidden shrink-0 flex items-center justify-center rounded-full bg-dark-gray dark:bg-dark-gray-dk hover:text-red-error dark:hover:text-red-error-dk transition-[width,opacity] duration-150 ${
                        isSelected
                          ? "w-[10px] opacity-100 pointer-events-auto"
                          : "w-0 opacity-0 pointer-events-none group-hover:w-[10px] group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:w-[10px] group-focus-within:opacity-100 group-focus-within:pointer-events-auto"
                      }`}
                    >
                      <CrossIcon className="w-[6px] h-[6px] shrink-0 text-pure-white dark:text-pure-white-dk stroke-3" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

    </div>
  );
};
